import { useEffect, useId, useMemo, useState } from 'react'
import { ownedSites, projectCollections, type OwnedSite, type ProjectCollection } from './data/catalog'
import { contributions } from './data/contributions'
import type { Project, ProjectCategory } from './data/projects'
import { useGithubProjects } from './hooks/useGithubProjects'

type Filter = 'All' | ProjectCategory
type Sort = 'updated' | 'stars' | 'name'
type BrowserItem =
  | { type: 'project'; project: Project }
  | { type: 'collection'; collection: ProjectCollection; projects: Project[] }

const filters: Filter[] = ['All', 'AI + ML', 'Hardware', 'Tools', 'Experiments']
const sorts: Sort[] = ['updated', 'stars', 'name']

function initialFilter(): Filter {
  if (typeof window === 'undefined') return 'All'
  const value = new URLSearchParams(window.location.search).get('category')
  return filters.includes(value as Filter) ? value as Filter : 'All'
}

function initialSort(): Sort {
  if (typeof window === 'undefined') return 'updated'
  const value = new URLSearchParams(window.location.search).get('sort')
  return sorts.includes(value as Sort) ? value as Sort : 'updated'
}

function initialQuery() {
  if (typeof window === 'undefined') return ''
  return new URLSearchParams(window.location.search).get('q') ?? ''
}

const languageColors: Record<string, string> = {
  'C++': '#315fc4',
  CSS: '#724f74',
  JavaScript: '#c99a09',
  Lua: '#315fc4',
  Python: '#e97838',
  TypeScript: '#0b6e69',
}

function ArrowIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20">
      <path d="M4 10h11M10 5l5 5-5 5" />
    </svg>
  )
}

function GithubIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M12 2a10 10 0 0 0-3.16 19.49c.5.1.68-.22.68-.48v-1.87c-2.78.6-3.37-1.18-3.37-1.18-.45-1.16-1.11-1.47-1.11-1.47-.9-.62.07-.61.07-.61 1 .07 1.52 1.03 1.52 1.03.89 1.52 2.33 1.08 2.9.83.09-.64.35-1.08.63-1.33-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02A9.58 9.58 0 0 1 12 6.83a9.5 9.5 0 0 1 2.5.34c1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.86v2.75c0 .27.18.58.69.48A10 10 0 0 0 12 2Z" />
    </svg>
  )
}

function SearchIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20">
      <circle cx="8.5" cy="8.5" r="5.5" />
      <path d="m13 13 4 4" />
    </svg>
  )
}

function StarIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20">
      <path d="m10 2.5 2.25 4.56 5.03.73-3.64 3.55.86 5.01L10 14l-4.5 2.35.86-5.01-3.64-3.55 5.03-.73L10 2.5Z" />
    </svg>
  )
}

function formatUpdated(value: string) {
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value))
}

function ProjectCard({ project }: { project: Project }) {
  const tooltipId = useId()
  const [isInspecting, setIsInspecting] = useState(false)
  const visibleTopics = project.topics.slice(0, 3)

  return (
    <article
      className={`project-card${project.feature ? ' project-card--popular' : ''}${project.visibility === 'supporting' ? ' project-card--supporting' : ''}`}
      data-category={project.category}
      data-inspect={isInspecting}
    >
      <div className="project-card__topline">
        <span className="category-label">{project.category}</span>
        <div className="inspect-wrap">
          <button
            className="inspect-button"
            type="button"
            aria-label={`Inspect details for ${project.name}`}
            aria-describedby={tooltipId}
            aria-expanded={isInspecting}
            onClick={() => setIsInspecting((value) => !value)}
            onBlur={() => setIsInspecting(false)}
          >
            <span aria-hidden="true">i</span>
          </button>
          <div className="inspect-tooltip" id={tooltipId} role="tooltip">
            <div className="tooltip-heading">
              <span>Repository scan</span>
              <i aria-hidden="true" />
            </div>
            <dl>
              <div>
                <dt>Source</dt>
                <dd>{project.fork ? 'Working fork' : 'Original project'}</dd>
              </div>
              <div>
                <dt>Forks</dt>
                <dd>{project.forks_count}</dd>
              </div>
              <div>
                <dt>Last signal</dt>
                <dd>{formatUpdated(project.updated_at)}</dd>
              </div>
            </dl>
            <p>{project.topics.length ? project.topics.join(' · ') : 'No GitHub topics added yet.'}</p>
          </div>
        </div>
      </div>

      <div className="project-card__title">
        <h2>
          <a href={project.html_url} target="_blank" rel="noreferrer">
            {project.name}
          </a>
        </h2>
        {project.feature && <span className="popular-label">Most watched</span>}
        {project.portfolioNote && <span className="supporting-label">{project.portfolioNote}</span>}
      </div>

      <p className="project-card__description">{project.description}</p>

      <div className="tag-list" aria-label={`Topics for ${project.name}`}>
        {visibleTopics.length ? (
          visibleTopics.map((topic) => <span key={topic}>#{topic}</span>)
        ) : (
          <span>#{project.category.toLowerCase().replaceAll(' ', '-')}</span>
        )}
        {project.topics.length > visibleTopics.length && <span>+{project.topics.length - visibleTopics.length}</span>}
      </div>

      <footer className="project-card__footer">
        <div className="card-facts">
          <span>
            <i style={{ background: languageColors[project.language ?? ''] ?? '#8dbeb8' }} />
            {project.language ?? 'Docs + data'}
          </span>
          <span>
            <StarIcon /> {project.stargazers_count}
          </span>
        </div>
        <div className="card-actions">
          {project.destinations?.map((destination) => (
            <a key={destination.url} href={destination.url} target="_blank" rel="noreferrer">
              {destination.label}
            </a>
          ))}
          <a href={project.html_url} target="_blank" rel="noreferrer" aria-label={`Open ${project.name} on GitHub`}>
            Open repo <ArrowIcon />
          </a>
        </div>
      </footer>
    </article>
  )
}

function ProjectCollectionCard({ collection, projects }: { collection: ProjectCollection; projects: Project[] }) {
  return (
    <article className="collection-card" data-accent={collection.accent} data-project-count={projects.length}>
      <header className="collection-card__header">
        <div>
          <span>{collection.eyebrow}</span>
          <h2>{collection.title}</h2>
        </div>
        <p>{projects.length} related {projects.length === 1 ? 'repository' : 'repositories'}</p>
      </header>
      <p className="collection-card__intro">
        {collection.description}
      </p>
      <div className="collection-projects">
        {projects.map((project, index) => (
          <section className="collection-project" key={project.name}>
            <span>{String(index + 1).padStart(2, '0')}</span>
            <h3>
              <a href={project.html_url} target="_blank" rel="noreferrer">
                {project.name}
              </a>
            </h3>
            <p>{project.description}</p>
            <footer>
              <span><i /> {project.language ?? 'Code'}</span>
              <a href={project.html_url} target="_blank" rel="noreferrer" aria-label={`Open ${project.name} on GitHub`}>
                Open <ArrowIcon />
              </a>
            </footer>
          </section>
        ))}
      </div>
    </article>
  )
}

function OwnedSiteCard({ site }: { site: OwnedSite }) {
  return (
    <article className="owned-site" data-accent={site.accent}>
      <span>{site.kind}</span>
      <h3>{site.name}</h3>
      <p>{site.description}</p>
      <a href={site.url} target="_blank" rel="noreferrer">
        {site.action} <ArrowIcon />
      </a>
    </article>
  )
}

function ContributionRow({ contribution }: { contribution: (typeof contributions)[number] }) {
  return (
    <article className="contribution-row">
      <div className="contribution-row__number">
        <span>Merged</span>
        <b>#{contribution.number}</b>
      </div>
      <div>
        <p>{contribution.kind}</p>
        <h3>{contribution.title}</h3>
        <span>{contribution.impact}</span>
      </div>
      <a href={contribution.url} target="_blank" rel="noreferrer" aria-label={`Open pull request ${contribution.number}`}>
        <ArrowIcon />
      </a>
    </article>
  )
}

function App() {
  const { projects, isLive } = useGithubProjects()
  const [activeFilter, setActiveFilter] = useState<Filter>(initialFilter)
  const [query, setQuery] = useState(initialQuery)
  const [sort, setSort] = useState<Sort>(initialSort)

  useEffect(() => {
    const url = new URL(window.location.href)
    query.trim() ? url.searchParams.set('q', query.trim()) : url.searchParams.delete('q')
    activeFilter === 'All' ? url.searchParams.delete('category') : url.searchParams.set('category', activeFilter)
    sort === 'updated' ? url.searchParams.delete('sort') : url.searchParams.set('sort', sort)
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
  }, [activeFilter, query, sort])

  const visibleItems = useMemo<BrowserItem[]>(() => {
    const normalizedQuery = query.trim().toLowerCase()
    const visibleProjects = projects.filter((project) => project.visibility !== 'hidden')
    const standaloneProjects = visibleProjects.filter((project) => !project.collection)
    const items: BrowserItem[] = standaloneProjects
      .filter((project) => {
      const matchesFilter = activeFilter === 'All' || project.category === activeFilter
      const matchesQuery =
        !normalizedQuery ||
        `${project.name} ${project.description} ${project.category} ${project.language ?? ''} ${project.topics.join(' ')}`
          .toLowerCase()
          .includes(normalizedQuery)

      return matchesFilter && matchesQuery
    })
      .map((project) => ({ type: 'project', project }))

    for (const collection of projectCollections) {
      const members = visibleProjects.filter((project) => project.collection === collection.id)
      if (!members.length) continue
      if (activeFilter !== 'All' && collection.category !== activeFilter) continue

      const collectionHaystack = `${collection.title} ${collection.eyebrow} ${collection.description} ${collection.searchTerms.join(' ')}`.toLowerCase()
      const collectionMatches = !normalizedQuery || collectionHaystack.includes(normalizedQuery)
      const matchingMembers = collectionMatches
        ? members
        : members.filter((project) => `${project.name} ${project.description} ${project.language ?? ''} ${project.topics.join(' ')}`.toLowerCase().includes(normalizedQuery))

      if (matchingMembers.length) items.push({ type: 'collection', collection, projects: matchingMembers })
    }

    const itemName = (item: BrowserItem) => item.type === 'project' ? item.project.name : item.collection.title
    const itemStars = (item: BrowserItem) => item.type === 'project'
      ? item.project.stargazers_count
      : item.projects.reduce((sum, project) => sum + project.stargazers_count, 0)
    const itemUpdated = (item: BrowserItem) => item.type === 'project'
      ? new Date(item.project.updated_at).getTime()
      : Math.max(...item.projects.map((project) => new Date(project.updated_at).getTime()))

    return items.sort((a, b) => {
      if (sort === 'stars') return itemStars(b) - itemStars(a) || itemName(a).localeCompare(itemName(b))
      if (sort === 'name') return itemName(a).localeCompare(itemName(b))
      return itemUpdated(b) - itemUpdated(a)
    })
  }, [activeFilter, projects, query, sort])

  const visibleRepositoryCount = visibleItems.reduce(
    (sum, item) => sum + (item.type === 'project' ? 1 : item.projects.length),
    0,
  )

  const totalStars = projects.reduce((sum, project) => sum + project.stargazers_count, 0)

  function resetControls() {
    setActiveFilter('All')
    setQuery('')
    setSort('updated')
  }

  return (
    <>
      <a className="skip-link" href="#project-grid">
        Skip to projects
      </a>

      <div className="site-shell" id="top">
        <header className="site-header">
          <a className="brand" href="#top" aria-label="PMZFX home">
            <span className="brand__mark">P/Z</span>
            <span>
              <b>PMZFX</b>
              <small>Project index</small>
            </span>
          </a>
          <p className="header-note">The technical side of TechMakesArt</p>
          <a className="github-link" href="https://github.com/PMZFX" target="_blank" rel="noreferrer">
            <GithubIcon />
            <span>GitHub</span>
          </a>
        </header>

        <main>
          <section className="intro" aria-labelledby="page-title">
            <div>
              <p className="eyebrow">Kato’s software, experiments, and technical rabbit holes</p>
              <h1 id="page-title">Find a project. See how it works.</h1>
              <p className="intro__lede">
                A browsable index of things I’ve built, tested, benchmarked, or taken apart—with AI as a tool and
                working results as the goal.
              </p>
            </div>
            <dl className="index-stats">
              <div>
                <dt>Public projects</dt>
                <dd>{projects.length}</dd>
              </div>
              <div>
                <dt>GitHub stars</dt>
                <dd>{totalStars}</dd>
              </div>
              <div>
                <dt>Index status</dt>
                <dd className={isLive ? 'is-live' : ''}>{isLive ? 'Live' : 'Snapshot'}</dd>
              </div>
            </dl>
          </section>

          <section className="project-browser" aria-labelledby="browser-title">
            <div className="browser-heading">
              <div>
                <span>01 / Browse</span>
                <h2 id="browser-title">Project index</h2>
              </div>
              <p>{visibleRepositoryCount} {visibleRepositoryCount === 1 ? 'repository' : 'repositories'} shown</p>
            </div>

            <div className="browser-controls">
              <label className="search-field">
                <SearchIcon />
                <span className="sr-only">Search projects</span>
                <input
                  type="search"
                  placeholder="Search projects, languages, or tags…"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
                {query && (
                  <button type="button" aria-label="Clear search" onClick={() => setQuery('')}>
                    Clear
                  </button>
                )}
              </label>
              <label className="sort-field">
                <span>Sort by</span>
                <select value={sort} onChange={(event) => setSort(event.target.value as Sort)}>
                  <option value="updated">Recently updated</option>
                  <option value="stars">Most starred</option>
                  <option value="name">Name A–Z</option>
                </select>
              </label>
            </div>

            <div className="filter-list" aria-label="Filter projects by category">
              {filters.map((filter) => {
                const count = filter === 'All' ? projects.length : projects.filter((project) => project.category === filter).length
                return (
                  <button
                    key={filter}
                    type="button"
                    className={activeFilter === filter ? 'is-active' : ''}
                    aria-pressed={activeFilter === filter}
                    onClick={() => setActiveFilter(filter)}
                  >
                    {filter} <span>{count}</span>
                  </button>
                )
              })}
            </div>

            <div className="project-grid" id="project-grid" aria-live="polite">
              {visibleItems.length ? (
                visibleItems.map((item) => item.type === 'project'
                  ? <ProjectCard key={item.project.name} project={item.project} />
                  : <ProjectCollectionCard key={item.collection.id} collection={item.collection} projects={item.projects} />,
                )
              ) : (
                <div className="empty-state">
                  <span aria-hidden="true">?</span>
                  <h3>No matching projects</h3>
                  <p>Try another term or reset the category filters.</p>
                  <button type="button" onClick={resetControls}>Reset the index</button>
                </div>
              )}
            </div>
          </section>

          <section className="owned-sites" aria-labelledby="owned-sites-title">
            <div className="owned-sites__heading">
              <div>
                <span>02 / Elsewhere</span>
                <h2 id="owned-sites-title">Sites with a job to do</h2>
              </div>
              <p>Games, specialist resources, and project stories that live beyond GitHub.</p>
            </div>
            <div className="owned-sites__grid">
              {ownedSites.map((site) => <OwnedSiteCard key={site.id} site={site} />)}
            </div>
          </section>

          <section className="contributions" aria-labelledby="contributions-title">
            <div className="contributions__intro">
              <div>
                <span>03 / Upstream</span>
                <h2 id="contributions-title">Contributions beyond my repos</h2>
              </div>
              <div className="contribution-summary">
                <strong>6 merged PRs</strong>
                <span>ggml-org / llama.cpp</span>
                <p>
                  Intel Arc and SYCL work focused on measurable inference performance, multi-GPU memory use, and reliability.
                </p>
              </div>
            </div>
            <div className="contribution-list">
              {contributions.map((contribution) => (
                <ContributionRow key={contribution.number} contribution={contribution} />
              ))}
            </div>
          </section>

          <aside className="working-note">
            <span>How I work</span>
            <p>Define the problem. Use the best available tools. Test the result. Document what matters.</p>
            <div className="working-note__actions">
              <a href="https://techmakesart.com" target="_blank" rel="noreferrer">
                More about Kato <ArrowIcon />
              </a>
              <a href="https://techmakesart.com/work-with-me" target="_blank" rel="noreferrer">
                Get in touch <ArrowIcon />
              </a>
            </div>
          </aside>
        </main>

        <footer className="site-footer">
          <div>
            <a className="brand brand--footer" href="#top">
              <span className="brand__mark">P/Z</span>
              <span><b>PMZFX</b></span>
            </a>
            <p>Ideas, made operational.</p>
          </div>
          <nav aria-label="Footer navigation">
            <a href="https://github.com/PMZFX" target="_blank" rel="noreferrer">GitHub</a>
            <a href="https://techmakesart.com" target="_blank" rel="noreferrer">TechMakesArt</a>
          </nav>
          <small>© {new Date().getFullYear()} PMZFX</small>
        </footer>
      </div>
    </>
  )
}

export default App
